import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios';
import { cleanUpSessionData, getNetlifyHeaders, getNetlifyTrustToken } from '@raisin/cp-utils';
import { isNetlifyTrustTokenNeeded } from './isNetlifyTrustTokenNeeded';

export type CustomAxiosRequestConfig = AxiosRequestConfig & {
  pathParams?: Record<string, string | number | boolean>;
};

const HTTP_RESPONSE_FORBIDDEN = 403;
const REQUEST_TIMEOUT = 60000;

const createClient = (baseURL: string, headers?: Record<string, string>) => {
  const api = axios.create({
    withCredentials: true,
    baseURL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  api.interceptors.request.use((config) => {
    // At this point, config is of type InternalAxiosRequestConfig
    const customConfig = config as typeof config & CustomAxiosRequestConfig;

    if (!config.url) {
      return config;
    }
    const currentUrl = new URL(config.url, customConfig.baseURL || undefined);

    // parse pathName to implement variables
    Object.entries(customConfig.pathParams || {}).forEach(([k, v]) => {
      currentUrl.pathname = currentUrl.pathname.replace(
        `:${k}`,
        encodeURIComponent(v as string | number | boolean),
      );
    });

    const finalUrlPath = `${customConfig.baseURL ? currentUrl.pathname : currentUrl.href}${
      currentUrl.search
    }`;

    const newHeaders = new AxiosHeaders(
      isNetlifyTrustTokenNeeded(config.url)
        ? { ...config.headers, ...getNetlifyHeaders({ trustToken: getNetlifyTrustToken() }) }
        : config.headers,
    );

    return {
      ...customConfig,
      url: finalUrlPath,
      headers: newHeaders,
    };
  });

  api.interceptors.response.use(
    (res) => res.data,
    (error) => {
      if (error.response) {
        //  The request was made and the server responded with a status code
        //  that falls out of the range of 2xx
        // eslint-disable-next-line no-console
        console.log(error.response.data);
        // eslint-disable-next-line no-console
        console.log(error.response.status);
        // eslint-disable-next-line no-console
        console.log(error.response.headers);

        if (error.response.status === HTTP_RESPONSE_FORBIDDEN) {
          cleanUpSessionData();
        }
        // throw error for all other 4xx and 5xx status codes
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        // eslint-disable-next-line no-console
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        // eslint-disable-next-line no-console
        console.log('Error', error.message);
      }
      // eslint-disable-next-line no-console
      console.log(error);
      return Promise.reject(error);
    },
  );

  return api;
};

export default createClient;
